import { Chip, InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react'
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss";
import SearchIcon from "@mui/icons-material/Search";

interface SearchFieldProps {
    value: string,
    handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    searchQuery: string,
    resetSearchValue: () => void,
    onSearch: (value: string[]) => void,
    isDisable?: boolean
} 

const SearchField: React.FC<SearchFieldProps> = (props) => {
    const { value, handleSearchChange, searchQuery, resetSearchValue, onSearch, isDisable } = props;
    
    const [searchChips, setSearchChips] = useState<string[]>([]);
    const chipContainerRef = useRef<HTMLDivElement | null>(null);
    const [focusedChipIndex, setFocusedChipIndex] = useState<number | null>(null);
    const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if(searchQuery){
            setSearchChips(searchQuery?.split(','))
        } else {
            setSearchChips([]);
        }
    }, [searchQuery]); 

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Create Chip on enter
        if (event.key === 'Enter' && value.trim()) {
            event.preventDefault();

            const newChips = [...searchChips, value.trim()];
            setSearchChips(newChips);
            onSearch(newChips);
            resetSearchValue();
        }

        // Delete Chip on backspace.
        if (event.key === 'Backspace') {
            // Delete last chip If there is no chip focused.
            if (!value && searchChips.length && focusedChipIndex == null) {
                const newChips = searchChips.slice(0, -1);

                setSearchChips(newChips);
                onSearch(newChips);
            }
        }

        // Move LEFT
        if (event.key === "ArrowLeft" && !value && searchChips.length) {
            event.preventDefault();

            setFocusedChipIndex((prev) => {
                const newIndex = prev === null
                    ? searchChips.length - 1
                    : Math.max(prev - 1, 0);

                setTimeout(() => {
                    chipRefs.current[newIndex]?.focus();
                    chipRefs.current[newIndex]?.scrollIntoView({
                        behavior: "smooth",
                        inline: "nearest",
                        block: "nearest"
                    });
                });

                return newIndex;
            });
        }

        // Move RIGHT
        if (event.key === "ArrowRight" && focusedChipIndex !== null) {
            event.preventDefault();

            const newIndex = focusedChipIndex + 1;

            if (newIndex >= searchChips.length) {
                setFocusedChipIndex(null);

                // Focus to textfield when it's last chip.
                inputRef.current?.focus();
                return;
            }

            setFocusedChipIndex(newIndex);

            setTimeout(() => {
                chipRefs.current[newIndex]?.focus();
                chipRefs.current[newIndex]?.scrollIntoView({
                    behavior: "smooth",
                    inline: "nearest",
                    block: "nearest"
                });
            });
        }
    };

    const handleDeleteChip = (chipIndex: number) => {
        const updatedChips = searchChips.filter((_, i) => i !== chipIndex);
        setSearchChips(updatedChips);
        onSearch(updatedChips);

        if(focusedChipIndex !== null){
            // No chips left → focus input
            if (updatedChips.length === 0) {
                setFocusedChipIndex(null);
    
                setTimeout(() => {
                    inputRef.current?.focus();
                });
    
                return;
            }
    
            // Focus next chip if exists otherwise previous
            const newIndex = chipIndex < updatedChips.length ? chipIndex : updatedChips.length - 1;
            setFocusedChipIndex(newIndex);
            setTimeout(() => {
                chipRefs.current[newIndex]?.focus();
                chipRefs.current[newIndex]?.scrollIntoView({
                    behavior: "smooth",
                    inline: "nearest"
                });
            });
        }
    }; 

    useEffect(() => {
        chipContainerRef.current?.scrollTo({
            left: chipContainerRef.current.scrollWidth,
            behavior: "smooth",
        });
    }, [searchChips]);
    
    return (
        <>
            <TextField
                fullWidth
                size="small"
                inputRef={inputRef}
                placeholder="Search.."
                value={value}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                helperText={value.trim() ? "Please hit enter to search." : ""}
                disabled={isDisable ?? false}
                InputProps={{
                    startAdornment: (
                        <>
                            <InputAdornment position="start" className={styles.adornmentWrapper}>
                                <SearchIcon fontSize="small" />

                                <div ref={chipContainerRef} className={styles.chipScrollContainer}>
                                    {searchChips.map((chip, index) => (
                                        <Chip
                                            key={index}
                                            label={chip}
                                            size="small"
                                            onDelete={() => handleDeleteChip(index)}
                                            className={styles.searchChip}
                                            variant={focusedChipIndex === index ? "filled" : "outlined"}
                                            ref={(el) => (chipRefs.current[index] = el)}
                                            tabIndex={-1}
                                        />
                                    ))}
                                </div>
                            </InputAdornment>
                        </>
                    ),
                }}
                className={styles.searchInput}
            />
        </>
    )
}

export default SearchField
